// Poppins font for MUI theme
import "@fontsource/poppins";
import "react-toastify/dist/ReactToastify.css";
import "./scss/style.scss";

import { BrowserRouter, Route, Switch } from "react-router-dom";
import React, { useEffect } from "react";
import { initializeSentry, setSentryUser } from "./config/sentry";

import { ChatStreamingProvider } from "./reusable/Chat/ChatStreaming/context/ChatStreamingContext";
import { ErrorBoundary } from "@sentry/react";
import { NucToastContainer } from "./reusable/components/Toast";
import { OAuthProvider } from "./helpers/OAuth/oauth";
import { PrivateRoute } from "./helpers/Auth";
import { QueryClientProvider } from "react-query";
import { TheLayout } from "./containers";
import { TheLayoutFallback } from "./reusable/components/Fallbacks";
import { queryClient } from "./config/react-query";
import { setupAxiosInterceptors } from "./config/axios";
import { initFeatureFlags } from "./config/featureFlags";
import {
  useGlobalSearchKeyboard,
  GlobalSearchModal,
} from "./features/globalSearch";
import PageTitleManager from "./components/PageTitleManager";

export let nuclearn_api_url = window.ENV.NUCLEARN_API_URL;
export let nuclearn_user_token = window.ENV.NUCLEARN_USER_TOKEN;
export let nuclearn_version = window.ENV.NUCLEARN_VERSION;
export let disable_raw_creds = window.ENV.DISABLE_RAW_CREDS;

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

// Pages
const Login = React.lazy(() => import("./views/pages/login/Login"));
const PasswordReset = React.lazy(
  () => import("./views/pages/password-reset/PasswordReset"),
);
const ResetEmailForm = React.lazy(
  () => import("./views/pages/password-reset/ResetEmailForm"),
);
const Register = React.lazy(() => import("./views/pages/register/Register"));
const Page404 = React.lazy(() => import("./views/pages/page404/Page404"));
const Page500 = React.lazy(() => import("./views/pages/page500/Page500"));
const SupportLogin = React.lazy(
  () => import("./views/pages/support-login/SupportLogin"),
);

initializeSentry();
setupAxiosInterceptors();
initFeatureFlags();

const App = () => {
  useGlobalSearchKeyboard();

  useEffect(() => {
    setSentryUser();
  }, []);

  return (
    // showDialog prop will show the dialog for users to submit a crash report
    <ErrorBoundary fallback={<TheLayoutFallback />}>
      <QueryClientProvider client={queryClient}>
        <OAuthProvider>
          <BrowserRouter>
            <PageTitleManager />
            <ChatStreamingProvider>
              <NucToastContainer autoClose={60000} hideProgressBar />
              <GlobalSearchModal />
              <React.Suspense fallback={loading}>
                <Switch>
                  <Route
                    exact
                    path="/login"
                    name="Login Page"
                    render={(props) => <Login {...props} />}
                  />
                  <Route
                    path="/password-reset-request"
                    name="Password Reset Request"
                    render={(props) => <ResetEmailForm {...props} />}
                  />
                  <Route
                    path="/password-reset"
                    name="Password Reset Form"
                    render={(props) => <PasswordReset {...props} />}
                  />
                  <Route
                    exact
                    path="/register"
                    name="Register Page"
                    render={(props) => <Register {...props} />}
                  />
                  <Route
                    exact
                    path="/404"
                    name="Page 404"
                    render={(props) => <Page404 {...props} />}
                  />
                  <Route
                    exact
                    path="/500"
                    name="Page 500"
                    render={(props) => <Page500 {...props} />}
                  />
                  <Route
                    exact
                    path="/support-login"
                    name="Support Login Page"
                    render={(props) => <SupportLogin {...props} />}
                  />

                  <PrivateRoute path="/">
                    <TheLayout />
                  </PrivateRoute>
                </Switch>
              </React.Suspense>
            </ChatStreamingProvider>
          </BrowserRouter>
        </OAuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
